import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  ChipProps,
  TextField,
} from "@mui/material";
import { forwardRef, useState } from "react";
import { GREY, VIOLET } from "@/config/mui/colorPalette";

export interface Option {
  id: string;
  label: string;
}

export type MultipleAutocompleteProps = Omit<
  AutocompleteProps<Option, true, false, false>,
  "renderInput" | "multiple"
> & {
  label: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  value?: Option[];
};

const CHIP_STYLES = {
  backgroundColor: VIOLET[40],
  color: VIOLET[100],
  "& .MuiChip-label": {
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: "1.25rem",
  },
  "& .MuiChip-deleteIcon": {
    color: VIOLET[100],
  },
};

export const PAPER_STYLES = {
  borderRadius: 3,
  boxShadow: "0px 4px 15px rgba(56, 19, 60, 0.25)",
  border: "1px solid",
  borderColor: GREY[20],
  mt: 1,
};

/**
 * A multi-select autocomplete component with custom styling and chip display.
 * Extends Material-UI's Autocomplete component with additional form field features.
 */
const MultipleAutocomplete = forwardRef<
  HTMLDivElement,
  MultipleAutocompleteProps
>(
  (
    {
      label,
      name,
      required = false,
      placeholder = "",
      error,
      helperText,
      onChange,
      value,
      slotProps,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");
    const safeValue = Array.isArray(value) ? value : [];

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    };

    const handleAutocompleteInputChange = (
      _: unknown,
      newInputValue: string
    ) => {
      setInputValue(newInputValue);
    };

    return (
      <Box>
        <Autocomplete
          {...props}
          value={safeValue}
          ref={ref}
          multiple
          getOptionKey={(option) => option.label + option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionDisabled={
            props.getOptionDisabled
              ? props.getOptionDisabled
              : (option) => safeValue.some((item) => item.id === option.id)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              fullWidth
              size={props.size}
              placeholder={placeholder}
              label={label}
              error={error}
              disabled={props.disabled}
              helperText={helperText}
              onChange={handleInputChange}
              value={inputValue}
              required={required}
            />
          )}
          onChange={onChange}
          onInputChange={handleAutocompleteInputChange}
          slotProps={{
            ...slotProps,
            paper: {
              sx: PAPER_STYLES,
              ...slotProps?.paper,
            },
            chip: {
              ...slotProps?.chip,
              sx: CHIP_STYLES,
              deleteIcon: <Close sx={{ width: 20, height: 20 }} />,
            } as ChipProps,
          }}
        />
      </Box>
    );
  }
);

MultipleAutocomplete.displayName = "MultipleAutocomplete";
export default MultipleAutocomplete;
